import {
  Event,
  type Definitie,
  type Projection,
  type Section,
  type WerkverzamelingRule,
} from '@eformulieren/model'
import { laagOf, ownLaag } from './projection.js'
import type { State } from './state.js'
import type { Difference } from './difference.js'

/**
 * De werkverzameling: wat deze persoon bij een overdracht moet bekijken
 * (R-176, ontwerp 7.5).
 *
 * Twee dingen liggen hier op elkaar en blijven uit elkaar. De **regels** komen uit het eisenboek:
 * bij terugleggen de secties met een openstaand criterium (R-35, C.1 S37), bij een hertoets de
 * secties met een openstaand criterium of een wijziging (R-189, C.1 S40). De **instelling** van de
 * inrichter is sinds het besluit van 31 oktober 2026 (D-59, ontwerp 8.6) nog maar één keuze: wat de
 * toetser bij een hertoets voorop krijgt, aanpassing gevraagd en gewijzigd of alleen aanpassing
 * gevraagd. Na indienen en na terugleggen is het vast. Deze functie neemt de regel daarom als
 * invoer en verzint er geen; welke regel geldt zegt {@link werkverzamelingRule}.
 *
 * **Het is een focusfilter bovenop de projectie, geen tweede projectie** (R-177).
 * De werkverzameling verbergt de rest niet: de overige secties zijn erbij te
 * halen. Ze bevat daarom nooit een sectie die niet in de projectie staat, en de
 * projectie wordt er niet kleiner van.
 */
/** De badge van R-98: openstaand criterium, gewijzigd, of beide. */
export type WerkverzamelingReason = 'openstaand-criterium' | 'gewijzigd' | 'beide'

/**
 * Welke regel geldt bij deze overdracht (D-59).
 *
 * Het begrip staat in het model en niet hier, want de definitie draagt de instelling: de
 * inrichter kiest hem per overdracht en deze functies rekenen ermee. Hier alleen doorgegeven,
 * zodat een afnemer van de engine hem niet uit twee pakketten hoeft te halen.
 */
export type { WerkverzamelingRule }

export interface WerkverzamelingSection {
  readonly sectieId: string
  readonly reden: WerkverzamelingReason
  /**
   * De velden waar het om gaat.
   *
   * Drie gevallen, in deze volgorde. De vragen die een openstaand criterium aanwijst
   * (`hoortBijVeld`, D-55) en de gewijzigde velden van deze sectie samen; is die verzameling
   * leeg, dan de hele sectie. Samen en niet met een voorrang, want "de velden waar het om gaat"
   * is bij een hertoets precies de som van die twee: wat NWO afwees en wat de invuller heeft
   * veranderd.
   *
   * Tot M6.19 stond hier dat het fijner niet kon omdat de koppeling naar het veld niet in het
   * model stond. Die staat er nu (`criterium.hoortBijVeld`), en een criterium dat er geen
   * aanwijst gedraagt zich als altijd: dan gaat het over de sectie die zijn toetssectie
   * aanwijst.
   */
  readonly veldIds: readonly string[]
}

export interface Werkverzameling {
  /** De secties met een reden: precies de badges die R-98 vraagt. */
  readonly secties: readonly WerkverzamelingSection[]
  /**
   * Of de ontvanger standaard alleen deze secties te zien krijgt.
   *
   * Bij de regel `alles` staat hij op false: er is dan geen focus, en de badges
   * per sectie blijven wel staan. Dat is de regel van een eerste toets (`ingediend`) en van de
   * eerste beurt. Zo blijft `reden` de drie waarden van R-98 en
   * hoeft er geen vierde waarde bij voor "geen bijzondere reden".
   */
  readonly gefilterd: boolean
}

export interface WerkverzamelingInput {
  /** De volledige definitie, voor de koppeling tussen een toetssectie en wat zij toetst. */
  readonly definitie: Definitie
  /** De projectie van deze persoon; de uitkomst blijft binnen haar secties (R-177). */
  readonly projectie: Projection
  /** De criteriumIds die in de vorige beurt op voldoet-niet stonden. */
  readonly openstaandeCriteria: readonly string[]
  /**
   * Het verschil tegen de vorige revisie (ontwerp 7.6). Telt alleen als de projectie die van
   * iemand is die toetst (`ownLaag`, R-88); voor een invuller rekent de functie zonder.
   */
  readonly verschil: Difference
  readonly regel: WerkverzamelingRule
}

/**
 * De voorgevulde keuze per gebeurtenis, uit het eisenboek (ontwerp 7.5).
 *
 * Bij terugleggen krijgt de invuller de secties met een openstaand criterium
 * (C.1 S37); bij een hertoets krijgt de toetser die plus de gewijzigde (C.1 S40).
 * Een eerste toets heeft geen vorige beurt om tegen af te zetten, dus daar is er
 * niets te filteren. Bij goedkeuren en afkeuren is het werk klaar en focust er
 * niets meer.
 */
export function defaultRule(event: Event): WerkverzamelingRule {
  switch (event) {
    case 'teruggelegd':
      return 'openstaande-criteria'
    case 'aangevuld':
    case 'opnieuw-ingediend':
      return 'openstaand-of-gewijzigd'
    case 'ingediend':
    case 'goedgekeurd':
    case 'afgekeurd':
      return 'alles'
  }
}

/**
 * Wat de configurator per overdracht aanbiedt, en daarmee wat de engine van een instelling
 * toepast (besluit van 31 oktober 2026, D-59, ontwerp 8.6).
 *
 * **Eén keuze, bij opnieuw indienen.** Na indienen is er nog niets getoetst, dus niets om voorop
 * te zetten (R-189 noemt de werkverzameling alleen bij een hertoets). Na terugleggen is het vast
 * gedrag en geen instelling (R-35): de invuller krijgt eerst wat aanpassing vraagt. Het hele
 * formulier is daar geen keuze meer, want dat spreekt R-35 tegen, en aanpassing gevraagd en
 * gewijzigd ook niet, want dat toont de invuller een verschil (R-88). Bij een hertoets kiest de
 * inrichter tussen de standaard van het eisenboek en een smallere focus; het hele formulier staat
 * er ook daar niet bij, want R-189 belooft de toetser bij een hertoets de werkverzameling.
 *
 * Aangevuld staat er niet bij. De pod legt die gebeurtenis nooit vast (een aanvulbeurt sluit met
 * opnieuw ingediend, `eventOnSubmit`); het model houdt het woord, zodat een bouwblok het later kan
 * terugbrengen, en dan hoort de keuze hier terug te komen. Goedgekeurd en afgekeurd evenmin: daar
 * is het werk klaar en is er geen ontvanger.
 *
 * De eerste regel van de lijst is de standaard, dezelfde als {@link defaultRule}.
 */
export const OFFERED_RULES: Readonly<Partial<Record<Event, readonly WerkverzamelingRule[]>>> = {
  'opnieuw-ingediend': ['openstaand-of-gewijzigd', 'openstaande-criteria'],
}

/** Biedt de configurator deze regel bij deze overdracht aan? */
export function isOfferedRule(event: Event, rule: WerkverzamelingRule): boolean {
  return OFFERED_RULES[event]?.includes(rule) === true
}

/**
 * De regel die geldt: een aangeboden instelling van de inrichter, anders die van het eisenboek
 * (D-59, R-35, R-189).
 *
 * **Een instelling die niet (meer) wordt aangeboden wordt bewaard en niet toegepast.** Tot 31
 * oktober 2026 bood de configurator per overdracht drie regels aan, en een definitie kan er dus
 * nog `teruggelegd: 'alles'` of een waarde bij `aangevuld` in hebben. Toepassen kan niet zonder
 * R-35 te breken: na terugleggen hoort de invuller eerst te krijgen wat aanpassing vraagt, en dat
 * is vast gedrag en geen instelling. Dus geldt hier de regel van het eisenboek. Weggooien gebeurt
 * ook niet: de waarde blijft in de definitie staan tot de inrichter hem zelf terugzet, en
 * {@link outdatedWerkverzameling} noemt hem, zodat de configurator er een melding met een knop bij
 * zet. Niet stil, en niet half: wat er staat en wat er geldt zijn allebei te zien.
 *
 * Een pod die al draait verandert hierdoor niet: hij draait de engine van zijn eigen image (R-101),
 * en daarin gold de oude lezing. Pas een nieuwe publicatie krijgt deze.
 *
 * **Zonder vorige beurt is het `alles`.** Dan is dit de eerste beurt en hoort de invuller het
 * hele formulier te zien; er is geen overdracht geweest, dus er is ook niets ingesteld om te
 * lezen. Dat is dezelfde uitkomst als `defaultRule('ingediend')` en toch een eigen tak, want
 * de reden is een andere: er is geen overdracht, niet een overdracht zonder focus.
 */
export function werkverzamelingRule(
  definitie: Pick<Definitie, 'werkverzameling'>,
  event: Event | undefined,
): WerkverzamelingRule {
  if (event === undefined) return 'alles'
  const set = definitie.werkverzameling?.[event]
  return set !== undefined && isOfferedRule(event, set) ? set : defaultRule(event)
}

/** Een instelling op de definitie die niet meer wordt aangeboden, met wat er in haar plaats geldt. */
export interface OutdatedWerkverzameling {
  readonly gebeurtenis: Event
  /** Wat er op de definitie staat. */
  readonly regel: WerkverzamelingRule
  /** Wat de engine toepast ({@link werkverzamelingRule}). */
  readonly geldt: WerkverzamelingRule
}

/**
 * De instellingen op deze definitie die de engine niet toepast, in de volgorde van de
 * gebeurtenissen (R-173). Leeg als alles klopt.
 *
 * Ook een waarde die gelijk is aan de regel die geldt telt mee, als ze niet wordt aangeboden
 * (`teruggelegd: 'openstaande-criteria'`): ze doet niets, en een instelling die niets doet hoort
 * de inrichter niet te laten denken dat hij iets heeft gekozen.
 */
export function outdatedWerkverzameling(
  definitie: Pick<Definitie, 'werkverzameling'>,
): readonly OutdatedWerkverzameling[] {
  const setting = definitie.werkverzameling ?? {}
  const out: OutdatedWerkverzameling[] = []
  for (const event of Event.options) {
    const rule = setting[event]
    if (rule === undefined || isOfferedRule(event, rule)) continue
    out.push({ gebeurtenis: event, regel: rule, geldt: werkverzamelingRule(definitie, event) })
  }
  return out
}

export function determineWerkverzameling(input: WerkverzamelingInput): Werkverzameling {
  const { definitie, projectie: projection, openstaandeCriteria: outstandingCriteria, verschil: given, regel: rule } = input
  const open = new Set(outstandingCriteria)
  /**
   * Het verschil telt alleen voor wie toetst (R-88).
   *
   * Buiten de toets wordt aan de invuller geen verschil getoond. Tot 31 oktober 2026 rekende deze
   * functie het verschil ook voor de invuller mee: wie na terugleggen een sectie aanpaste die
   * aanpassing vroeg, zag na herladen de badge "Aanpassing gevraagd en gewijzigd", en dat is het
   * verschil van R-88 via de achterdeur. Hier en niet bij de pod en het voorbeeld apart: beide
   * geven hetzelfde verschil door, en één grens op één plek kan niet uit de pas lopen.
   */
  const difference: Difference = ownLaag(projection) === 'toets' ? given : NO_DIFFERENCE

  /** Per sectie-id: staat er een eigen criterium open? */
  const hasOpenCriterion = new Map<string, boolean>()
  /** Per getoetste sectie: de toetssecties die er een uitspraak over doen (R-109, D-55). */
  const reviewedBy = new Map<string, string[]>()

  /**
   * De vragen die een openstaand criterium aanwijst (D-55).
   *
   * Over alle secties heen en niet per sectie: een toetscriterium doet een uitspraak over een
   * vraag van het formulier dat wordt getoetst, en die staat per definitie in een andere sectie
   * dan het criterium zelf.
   */
  const openFields = new Set<string>()

  for (const section of definitie.secties) {
    hasOpenCriterion.set(
      section.id,
      section.velden.some((field) => field.control === 'criterium' && open.has(field.criteriumId)),
    )
    for (const field of section.velden) {
      if (field.control !== 'criterium' || !open.has(field.criteriumId)) continue
      if (field.hoortBijVeld !== undefined) openFields.add(field.hoortBijVeld)
    }
    if (section.type === 'toets' && section.hoortBij !== undefined) {
      reviewedBy.set(section.hoortBij, [...(reviewedBy.get(section.hoortBij) ?? []), section.id])
    }
  }

  const sections: WerkverzamelingSection[] = []
  for (const section of projection.secties) {
    const openHere = outstanding(section, hasOpenCriterion, reviewedBy)
    const reason = chooseReason(openHere, changed(section, difference, definitie))
    if (reason === null) continue
    // Bij terugleggen focust de invuller op wat NWO afwees, niet op wat hij zelf
    // heeft veranderd (C.1 S37).
    if (rule === 'openstaande-criteria' && !openHere) continue

    const ownChanged = section.velden.filter((field) => difference.velden.has(field.id)).map((field) => field.id)
    const designated = section.velden.filter((field) => openFields.has(field.id)).map((field) => field.id)
    // Een `Set` en niet twee lijsten achter elkaar: een veld dat is gewijzigd én door een
    // criterium wordt aangewezen hoort er één keer in te staan.
    const relevant = [...new Set([...designated, ...ownChanged])]
    sections.push({
      sectieId: section.id,
      reden: reason,
      veldIds: relevant.length > 0 ? relevant : section.velden.map((field) => field.id),
    })
  }

  return { secties: sections, gefilterd: rule !== 'alles' }
}

/**
 * Staat er op deze sectie een criterium open?
 *
 * Voor een toetssectie is dat een eigen criterium; voor een gewone sectie is dat
 * een criterium in de toetssectie die haar toetst, want dat is de sectie die de
 * invuller moet aanpassen (C.1 S37).
 */
function outstanding(
  section: Section,
  hasOpenCriterion: ReadonlyMap<string, boolean>,
  reviewedBy: ReadonlyMap<string, string[]>,
): boolean {
  if (hasOpenCriterion.get(section.id) === true) return true
  return (reviewedBy.get(section.id) ?? []).some((reviewSectionId) => hasOpenCriterion.get(reviewSectionId) === true)
}

/**
 * Is er in deze sectie iets gewijzigd?
 *
 * Voor een toetssectie telt ook een wijziging in de sectie die zij toetst: dat is
 * precies waar de hertoets naar kijkt (C.1 S40).
 */
function changed(section: Section, difference: Difference, definitie: Definitie): boolean {
  if (difference.secties.has(section.id)) return true
  if (section.type !== 'toets' || section.hoortBij === undefined) return false
  const exists = definitie.secties.some((other) => other.id === section.hoortBij)
  return exists && difference.secties.has(section.hoortBij)
}

/** Geen verschil: voor wie niet toetst (R-88). */
const NO_DIFFERENCE: Difference = { velden: new Set(), secties: new Set() }

function chooseReason(outstandingHere: boolean, changedHere: boolean): WerkverzamelingReason | null {
  if (outstandingHere && changedHere) return 'beide'
  if (outstandingHere) return 'openstaand-criterium'
  if (changedHere) return 'gewijzigd'
  return null
}

/**
 * Eén opmerking van NWO bij een sectie van de invuller (R-75).
 *
 * Hier en niet bij de pod of de renderer alleen: beide stelden tot nu toe dezelfde vraag met
 * een eigen kopie van dit type. De pod leest hem uit een echte `voorgangerRevisie`, het
 * voorbeeld van de configurator uit een zelf vastgelegde ijkstand (`voorbeeld.ts`); dezelfde
 * vraag op een ander moment, dus één type en niet twee.
 */
export interface Comment {
  /** De sectie van de invuller waar de opmerking over gaat. */
  readonly sectieId: string
  /** Het toetsveld waaruit ze komt, zodat de renderer haar label kan tonen. */
  readonly veldId: string
  readonly tekst: string
}

/**
 * De toetsvelden die teruggaan naar de indiener, met de sectie waar ze over gaan (R-75).
 *
 * Een toetssectie zegt met `hoortBij` over welke sectie ze een uitspraak doet (R-109, D-55).
 * Zonder dat is er geen plek om de opmerking te tonen, dus zo'n veld reist niet mee; de
 * definitie staat het toe en de weergave kan er niets met doen.
 *
 * Verplaatst uit `apps/runtime/src/werkverzameling.ts`: de pod stelt deze vraag bij een echte
 * overdracht, het voorbeeld van de configurator bij een zelf getypte opmerking. Eén regel,
 * twee lezers. Sinds terugleggen een opmerking vraagt is er een derde: `teruggavenIn` in
 * `overdrachtbeleid.ts`, dat over de secties van een projectie loopt en daarom alleen de secties
 * vraagt en niet de hele definitie.
 */
export function returningFields(
  definitie: { readonly secties: readonly Section[] },
): readonly { veldId: string; overSectie: string }[] {
  const out: { veldId: string; overSectie: string }[] = []
  for (const section of definitie.secties) {
    if (section.type !== 'toets' || section.hoortBij === undefined) continue
    for (const field of section.velden) {
      if ('gaatTerug' in field && field.gaatTerug === true) {
        out.push({ veldId: field.id, overSectie: section.hoortBij })
      }
    }
  }
  return out
}

/**
 * De secties waarover wordt vergeleken: alles behalve de toetslaag (R-77).
 *
 * **De toetslaag hoort er niet bij, en dat is niet vanzelfsprekend.** R-77 zegt wat de toetser
 * bij de hertoets ziet: welke velden *de invuller* heeft gewijzigd. Zijn eigen aantekeningen van
 * de vorige ronde zijn dat niet. En het is geen smaakvraag: een toetsbeurt is een eigen instantie
 * met een eigen, lege toetslaag, dus tegen de ijkstand van de vorige toetsbeurt gehouden zijn
 * *alle* toetsvelden verdwenen. Zonder deze grens draagt de toetssectie bij elke hertoets de
 * badge gewijzigd, en dan zegt de badge niets meer.
 *
 * Verplaatst uit `apps/runtime/src/werkverzameling.ts`, om dezelfde reden als `returningFields`.
 */
export function sectionsToCompare(definitie: Definitie): readonly Section[] {
  return definitie.secties.filter((section) => laagOf(section.type) !== 'toets')
}

/**
 * De opmerkingen van NWO, uit een bevroren stand (R-75).
 *
 * `stand` is de bevroren antwoordenkaart waarin de opmerking staat: bij de pod de
 * `voorgangerRevisie.antwoorden` (de toetsbeurt die net sloot), bij het voorbeeld van de
 * configurator de ijkstand die het paneel zelf heeft vastgelegd. Beide dragen dezelfde vorm
 * (`Stand`), dus is dit dezelfde lezing op een ander moment.
 */
export function commentsFrom(
  definitie: Definitie,
  projection: Projection,
  state: State | undefined,
): readonly Comment[] {
  if (state === undefined) return []
  const visible = new Set(projection.secties.map((section) => section.id))
  const out: Comment[] = []
  for (const { veldId: fieldId, overSectie: aboutSection } of returningFields(definitie)) {
    // R-179: eerst projecteren. Mag deze lezer de getoetste sectie niet zien, dan hoort de
    // opmerking erover ook niet in beeld te komen.
    if (!visible.has(aboutSection)) continue
    const response = state[fieldId]
    if (response === undefined) continue
    const text = 'waarde' in response ? response.waarde : undefined
    if (typeof text !== 'string' || text.trim() === '') continue
    out.push({ sectieId: aboutSection, veldId: fieldId, tekst: text })
  }
  return out
}
